import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import type {
  ProjectCompaniesSort,
  ProjectListing,
  ProjectListingsResponse,
} from '@/api/v4/projects.api';
import { fetchProjectListings } from '@/api/v4/projects.api';
import { QueryKey } from '@/config/query-client';
import { getTimeInMs } from '@/helpers/get-time-in-ms';
import type { ISort } from '@/ui/table/table.types';

import { projectListingSortState } from '../project-listing.state';
import { buildProjectListingsQueryMeta } from './project-listings-query-meta';
import { useListingsSearchParam } from './use-listings-search-param';

interface ProjectListingsProps {
  enabled?: boolean;
  pageSize?: number;
  projectId?: string | null;
  search?: string;
  sort?: ISort<ProjectCompaniesSort | undefined>;
  stageFilter?: string[];
}

const DEFAULT_PAGE_SIZE = 20;

export const useProjectListings = ({
  enabled = true,
  pageSize = DEFAULT_PAGE_SIZE,
  projectId,
  search,
  sort,
  stageFilter,
}: ProjectListingsProps) => {
  const projectListingsSort = useAtomValue(projectListingSortState);
  const sortParams = sort ?? projectListingsSort;
  const { listingsSearchParam } = useListingsSearchParam();

  const queryKey = useMemo(
    () => [
      QueryKey.ProjectListings,
      projectId,
      pageSize,
      ...(sortParams ? [sortParams] : []),
      ...(stageFilter ? stageFilter : []),
      search ? [search] : listingsSearchParam ? [listingsSearchParam] : [],
    ],
    [listingsSearchParam, pageSize, projectId, search, sortParams, stageFilter],
  );

  const query = useInfiniteQuery<ProjectListingsResponse | null>({
    initialPageParam: 1,
    enabled: Boolean(projectId) && enabled,
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined;

      const nextPage =
        lastPage.currentPage + 1 <= lastPage.totalPagesCount
          ? lastPage.currentPage + 1
          : undefined;

      return nextPage;
    },
    meta: buildProjectListingsQueryMeta(stageFilter),
    queryFn: ({ pageParam }) => {
      if (!projectId) return null;

      const params = {
        ...sortParams,
        direction: sortParams?.direction ?? 'desc',
        items: pageSize,
        listingStageIds: stageFilter ? stageFilter : undefined,
        page: pageParam as number,
        search: search ?? listingsSearchParam ?? undefined,
        sort: sortParams?.sort ?? 'createdDate',
      };

      return fetchProjectListings(projectId, params);
    },
    queryKey,
    staleTime: getTimeInMs(5, 'minute'),
  });

  const projectListings: ProjectListing[] = useMemo(() => {
    const all = query.data?.pages.flatMap(page => page?.records ?? []) ?? [];
    const seen = new Set<string>();
    return all.filter(listing => {
      if (seen.has(listing.id)) {
        return false;
      }
      seen.add(listing.id);
      return true;
    });
  }, [query.data]);

  return {
    projectListings,
    ...query,
  };
};
